/**
 * Formulário de um veículo da frota.
 *
 * Papel: preenche, valida e grava um veículo — código, marca, modelo, ano,
 * placa, chassi, renavam, categoria, lotação, código FIPE, seguro e blindagem.
 *
 * Como usar: quem monta este componente passa `veiculo` para editar ou `null`
 * para criar, e deverá dar a ele uma `key` diferente por veículo. Sem a `key`,
 * o React reaproveita a mesma instância ao trocar de registro e os campos
 * continuam com o que a pessoa via antes.
 *
 * Marcar **fora da apólice** limpa a classe de bônus e desabilita o campo: as
 * duas informações se excluem, e o schema recusa a combinação em que as duas
 * vêm preenchidas. Deixar o campo habilitado faria a pessoa digitar um número
 * que a gravação depois recusaria.
 *
 * A placa aceita os dois padrões brasileiros — `ABC1D23` e `ABC1234` — e é
 * guardada em maiúsculas e sem pontuação; quem confere é `placaSchema`.
 */
import { type NovoVeiculo, type Veiculo, novoVeiculoSchema } from '@concierge/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { Controller, type FieldPath, useForm, useWatch } from 'react-hook-form';

import { aplicarErroDaApi, tradutorPorMapa } from '@/shared/lib/erro-de-formulario';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import { DialogFooter } from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import { useSalvarVeiculo } from '../hooks/usar-veiculos';
import { VEICULO_EM_BRANCO, type VeiculoNoFormulario } from '../types';

interface FormularioVeiculoProps {
  /** O veículo a editar, ou `null` para criar um novo. */
  veiculo: Veiculo | null;
  onSalvo: () => void;
  onCancelar: () => void;
}

/** Os campos que a API pode apontar, cada um no caminho do formulário. */
const CAMPOS: Record<string, FieldPath<VeiculoNoFormulario> | undefined> = {
  codigo: 'codigo',
  marca: 'marca',
  modelo: 'modelo',
  ano: 'ano',
  placa: 'placa',
  chassi: 'chassi',
  renavam: 'renavam',
  categoria: 'categoria',
  lotacao: 'lotacao',
  fipe: 'fipe',
  classeBonus: 'classeBonus',
  foraDaApolice: 'foraDaApolice',
  blindado: 'blindado',
  observacao: 'observacao',
};

/**
 * Converte o veículo vindo da API no formato que o formulário guarda.
 *
 * Os campos numéricos viram string porque é isso que um `<input>` guarda; o
 * schema os converte de volta no envio, e `null` vira string vazia para o campo
 * não nascer com zero.
 *
 * @param veiculo - O veículo a editar, ou `null` para começar em branco.
 * @returns Os valores iniciais.
 */
function valoresIniciais(veiculo: Veiculo | null): VeiculoNoFormulario {
  if (veiculo === null) return { ...VEICULO_EM_BRANCO };

  return {
    codigo: veiculo.codigo,
    marca: veiculo.marca,
    modelo: veiculo.modelo,
    ano: veiculo.ano === null ? '' : String(veiculo.ano),
    placa: veiculo.placa,
    chassi: veiculo.chassi ?? '',
    renavam: veiculo.renavam ?? '',
    categoria: veiculo.categoria,
    lotacao: veiculo.lotacao === null ? '' : String(veiculo.lotacao),
    fipe: veiculo.fipe ?? '',
    classeBonus: veiculo.classeBonus === null ? '' : String(veiculo.classeBonus),
    foraDaApolice: veiculo.foraDaApolice,
    blindado: veiculo.blindado,
    observacao: veiculo.observacao ?? '',
  };
}

/**
 * O formulário de criação e de edição de veículo.
 *
 * @param veiculo - O veículo a editar, ou `null` para criar.
 * @param onSalvo - Chamado depois de a API confirmar a gravação.
 * @param onCancelar - Chamado quando se desiste, sem gravar nada.
 * @returns O formulário completo, com os botões de ação.
 */
export function FormularioVeiculo({ veiculo, onSalvo, onCancelar }: FormularioVeiculoProps) {
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const salvar = useSalvarVeiculo();

  const form = useForm<VeiculoNoFormulario, unknown, NovoVeiculo>({
    resolver: zodResolver(novoVeiculoSchema),
    defaultValues: valoresIniciais(veiculo),
  });

  const { errors } = form.formState;
  const enviando = salvar.isPending;
  const foraDaApolice = useWatch({ control: form.control, name: 'foraDaApolice' });
  const { setValue } = form;

  useEffect(() => {
    // Quem sai da apólice perde a classe de bônus: o schema recusa os dois
    // preenchidos, e limpar aqui evita que a pessoa descubra isso só ao salvar.
    if (foraDaApolice === true) setValue('classeBonus', '');
  }, [foraDaApolice, setValue]);

  /** Grava o veículo e leva o erro da API para o campo que o causou. */
  const handleSalvar = form.handleSubmit(async (dados) => {
    setErroGeral(null);
    try {
      await salvar.mutateAsync({ id: veiculo?.id ?? null, dados });
      onSalvo();
    } catch (erro) {
      setErroGeral(aplicarErroDaApi(erro, form, tradutorPorMapa(CAMPOS)));
    }
  });

  return (
    <form onSubmit={handleSalvar}>
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="veiculo-codigo">Código</Label>
          <Input
            id="veiculo-codigo"
            autoComplete="off"
            placeholder="V01"
            disabled={enviando}
            {...form.register('codigo')}
          />
          {errors.codigo && (
            <p className="text-sm text-destructive" role="alert">
              {errors.codigo.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="veiculo-marca">Marca</Label>
          <Input
            id="veiculo-marca"
            autoComplete="off"
            disabled={enviando}
            {...form.register('marca')}
          />
          {errors.marca && (
            <p className="text-sm text-destructive" role="alert">
              {errors.marca.message}
            </p>
          )}
        </div>

        <div className="col-span-full flex flex-col gap-1.5">
          <Label htmlFor="veiculo-modelo">Modelo</Label>
          <Input
            id="veiculo-modelo"
            autoComplete="off"
            placeholder="Como está no documento do veículo"
            disabled={enviando}
            {...form.register('modelo')}
          />
          {errors.modelo && (
            <p className="text-sm text-destructive" role="alert">
              {errors.modelo.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="veiculo-ano">Ano</Label>
          <Input
            id="veiculo-ano"
            inputMode="numeric"
            autoComplete="off"
            disabled={enviando}
            {...form.register('ano')}
          />
          {errors.ano && (
            <p className="text-sm text-destructive" role="alert">
              {errors.ano.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="veiculo-placa">Placa</Label>
          <Input
            id="veiculo-placa"
            autoComplete="off"
            placeholder="ABC1D23"
            disabled={enviando}
            {...form.register('placa')}
          />
          {errors.placa && (
            <p className="text-sm text-destructive" role="alert">
              {errors.placa.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="veiculo-chassi">Chassi</Label>
          <Input
            id="veiculo-chassi"
            autoComplete="off"
            placeholder="Ex: 9BW..."
            disabled={enviando}
            {...form.register('chassi')}
          />
          {errors.chassi && (
            <p className="text-sm text-destructive" role="alert">
              {errors.chassi.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="veiculo-renavam">Renavam</Label>
          <Input
            id="veiculo-renavam"
            inputMode="numeric"
            autoComplete="off"
            placeholder="11 dígitos"
            disabled={enviando}
            {...form.register('renavam')}
          />
          {errors.renavam && (
            <p className="text-sm text-destructive" role="alert">
              {errors.renavam.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="veiculo-categoria">Categoria</Label>
          <Input
            id="veiculo-categoria"
            autoComplete="off"
            placeholder="Ex: SUV, Sedan, Moto"
            disabled={enviando}
            {...form.register('categoria')}
          />
          {errors.categoria && (
            <p className="text-sm text-destructive" role="alert">
              {errors.categoria.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="veiculo-lotacao">Lugares</Label>
          <Input
            id="veiculo-lotacao"
            inputMode="numeric"
            autoComplete="off"
            disabled={enviando}
            {...form.register('lotacao')}
          />
          {errors.lotacao && (
            <p className="text-sm text-destructive" role="alert">
              {errors.lotacao.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="veiculo-fipe">Código FIPE</Label>
          <Input
            id="veiculo-fipe"
            autoComplete="off"
            placeholder="000000-0"
            disabled={enviando}
            {...form.register('fipe')}
          />
          {errors.fipe && (
            <p className="text-sm text-destructive" role="alert">
              {errors.fipe.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="veiculo-classe-bonus">Classe de bônus</Label>
          <Input
            id="veiculo-classe-bonus"
            inputMode="numeric"
            autoComplete="off"
            placeholder="0 a 10"
            disabled={enviando || foraDaApolice === true}
            {...form.register('classeBonus')}
          />
          {errors.classeBonus && (
            <p className="text-sm text-destructive" role="alert">
              {errors.classeBonus.message}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Controller
            control={form.control}
            name="blindado"
            render={({ field }) => (
              <Checkbox
                id="veiculo-blindado"
                checked={field.value === true}
                onCheckedChange={(marcado) => {
                  field.onChange(marcado === true);
                }}
                disabled={enviando}
              />
            )}
          />
          <Label htmlFor="veiculo-blindado" className="font-normal">
            Blindado
          </Label>
        </div>

        <div className="flex items-center gap-2">
          <Controller
            control={form.control}
            name="foraDaApolice"
            render={({ field }) => (
              <Checkbox
                id="veiculo-fora-da-apolice"
                checked={field.value === true}
                onCheckedChange={(marcado) => {
                  field.onChange(marcado === true);
                }}
                disabled={enviando}
              />
            )}
          />
          <Label htmlFor="veiculo-fora-da-apolice" className="font-normal">
            Fora da apólice
          </Label>
        </div>

        <div className="col-span-full flex flex-col gap-1.5">
          <Label htmlFor="veiculo-observacao">Observações</Label>
          <Textarea
            id="veiculo-observacao"
            rows={3}
            disabled={enviando}
            {...form.register('observacao')}
          />
          {errors.observacao && (
            <p className="text-sm text-destructive" role="alert">
              {errors.observacao.message}
            </p>
          )}
        </div>

        {erroGeral !== null && (
          <p className="col-span-full text-sm text-destructive" role="alert">
            {erroGeral}
          </p>
        )}
      </div>

      <DialogFooter>
        <Button type="button" variant="ghost" disabled={enviando} onClick={onCancelar}>
          Cancelar
        </Button>
        <Button type="submit" disabled={enviando}>
          {enviando ? 'Salvando…' : 'Salvar veículo'}
        </Button>
      </DialogFooter>
    </form>
  );
}
